(() => {
  if (window.__meltProductCrossSellLoaded) return;
  window.__meltProductCrossSellLoaded = true;

  class MeltProductCrossSell {
    constructor(section) {
      this.section = section;
      this.currentKind = section.dataset.currentKind || 'scent';
      this.currentProductId = String(section.dataset.currentProductId || '');
      this.currentSignature = section.dataset.currentSignature || '';
      this.country = String(section.dataset.country || 'DE').toUpperCase();
      this.cartJsonUrl = section.dataset.cartJsonUrl || '/cart.js';
      this.cartAddUrl = section.dataset.cartAddUrl || '/cart/add.js';
      this.candidates = Array.from(section.querySelectorAll('[data-md-pdp-candidate]'));
      this.status = section.querySelector('[data-md-pdp-status]');

      section.addEventListener('click', (event) => {
        const button = event.target.closest('[data-md-pdp-add]');
        if (!button || !section.contains(button)) return;
        event.preventDefault();
        this.addProduct(button);
      });

      this.initialize();
    }

    normalizeSignal(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
    }

    inferScentFamilies(value) {
      const signature = this.normalizeSignal(value);
      const families = new Set();
      const familyTerms = {
        citrus: /zitrone|lemon|limette|lime|citrus|bergamott|grapefruit|orange|mandarin|yuzu/,
        fruity: /beere|berry|pfirsich|peach|melone|melon|ananas|pineapple|mango|apfel|apple|kirsche|cherry|erdbeer|strawberry|passion|frucht|fruity|kokos|coconut/,
        tropical: /tropisch|tropical|ananas|pineapple|mango|passion|kokos|coconut|morocco|marokko/,
        gourmand: /kaffee|coffee|waffle|karamell|caramel|vanille|vanilla|marsh|bakery|back|cake|kuchen|cookie|brunch|bun|latte|schokolade|chocolate|zimt|cinnamon|pumpkin spice/,
        floral: /jasmin|jasmine|rose|lavendel|lavender|veilchen|violet|blumig|floral|lilie|lily|pfingstrose|peony/,
        fresh: /frisch|fresh|clean|laundry|wasche|cotton|baumwolle|linen|leinen|breeze|brise|ozon|ocean|meer|aqua|spa|clarity/,
        woody: /holz|wood|sandel|sandal|zeder|cedar|amber|moschus|musk|patchouli|oud|tabak|tobacco|tonka|weihrauch|incense|gewurz|spice/,
        cozy: /cozy|cosy|pyjama|pajama|warm|cashmere|kaschmir|marsh|waffle|coffee|kaffee|brunch|vanille|vanilla|amber|tonka/,
      };

      Object.entries(familyTerms).forEach(([family, pattern]) => {
        if (pattern.test(signature)) families.add(family);
      });
      return families;
    }

    seasonalFamilies() {
      const month = new Date().getMonth() + 1;
      if (month === 12 || month <= 2) return new Set(['cozy', 'gourmand', 'woody']);
      if (month <= 5) return new Set(['floral', 'fresh', 'citrus']);
      if (month <= 8) return new Set(['fruity', 'citrus', 'fresh', 'tropical']);
      return new Set(['cozy', 'gourmand', 'woody']);
    }

    hashSignal(value) {
      return Array.from(String(value || '')).reduce((hash, character) => {
        return ((hash << 5) - hash + character.charCodeAt(0)) | 0;
      }, 0);
    }

    familiesOverlap(first, second) {
      return Array.from(first).some((family) => second.has(family));
    }

    complementaryFamilyScore(candidateFamilies, currentFamilies) {
      const complements = {
        citrus: ['fruity', 'fresh', 'tropical'],
        fruity: ['citrus', 'fresh', 'floral', 'tropical'],
        tropical: ['citrus', 'fruity', 'fresh'],
        gourmand: ['cozy', 'woody'],
        floral: ['fresh', 'fruity'],
        fresh: ['citrus', 'fruity', 'floral', 'tropical'],
        woody: ['cozy', 'gourmand'],
        cozy: ['gourmand', 'woody'],
      };
      let score = 0;

      currentFamilies.forEach((family) => {
        (complements[family] || []).forEach((complement) => {
          if (candidateFamilies.has(complement)) score += 2;
        });
      });
      return Math.min(6, score);
    }

    itemSignature(item) {
      return [
        item && item.product_title,
        item && item.title,
        item && item.variant_title,
        item && item.handle,
        item && item.product_type,
        item && item.vendor,
        item && item.product_description,
      ].filter(Boolean).join(' ').toLowerCase();
    }

    hasSensitiveCartItem(cart) {
      return (cart.items || []).some((item) => {
        return Boolean(item.selling_plan_allocation) ||
          /adventskalender|preorder|vorbestellung|subscription|monthly mëlt box/.test(this.itemSignature(item));
      });
    }

    scoreCandidate(card, context, selected) {
      const signature = card.dataset.recommendationSignature || '';
      const candidateFamilies = this.inferScentFamilies(signature);
      const bestsellerRank = Math.max(1, Number(card.dataset.bestsellerRank || 8));
      let score = Math.max(2, 24 - ((bestsellerRank - 1) * 2.35));

      candidateFamilies.forEach((family) => {
        if (context.seasonalFamilies.has(family)) score += 2.25;
        if (context.currentFamilies.has(family)) score += 5;
      });
      score += this.complementaryFamilyScore(candidateFamilies, context.currentFamilies);

      if (card.dataset.recommendationKind === 'lamp') {
        const month = new Date().getMonth() + 1;
        const normalized = this.normalizeSignal(signature);
        const bright = /white|weiss|creme|gray|grau|glass|glas/.test(normalized);
        const atmospheric = /noir|black|schwarz|smokey|bronze|midas|gold|mercury/.test(normalized);
        if (bright && month >= 3 && month <= 8) score += 2.5;
        if (atmospheric && (month >= 9 || month <= 2)) score += 2.5;
      }

      if (selected.length > 0 && card.dataset.recommendationKind === 'scent') {
        const selectedFamilies = this.inferScentFamilies(selected[0].dataset.recommendationSignature || '');
        if (candidateFamilies.size > 0 && selectedFamilies.size > 0) {
          score += this.familiesOverlap(candidateFamilies, selectedFamilies) ? -3 : 2.5;
        }
      }

      const rotationSignal = [
        this.currentProductId,
        context.rotationWindow,
        this.country,
        card.dataset.productId,
      ].join('|');
      score += (Math.abs(this.hashSignal(rotationSignal)) % 650) / 100;
      return score;
    }

    async fetchCart() {
      try {
        const response = await fetch(this.cartJsonUrl, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error('Cart could not be loaded');
        return await response.json();
      } catch (error) {
        return { items: [], item_count: 0 };
      }
    }

    selectRecommendations(cart) {
      this.candidates.forEach((card) => {
        card.hidden = true;
        card.style.removeProperty('order');
      });

      if (this.hasSensitiveCartItem(cart)) {
        this.section.hidden = true;
        return;
      }

      const cartProductIds = new Set((cart.items || []).map((item) => String(item.product_id)));
      const plan = this.currentKind === 'lamp' ? ['scent', 'scent'] : ['lamp', 'scent'];
      const selected = [];
      const context = {
        currentFamilies: this.inferScentFamilies(this.currentSignature),
        seasonalFamilies: this.seasonalFamilies(),
        rotationWindow: Math.floor(Date.now() / 259200000),
      };

      const pickCandidate = (kind) => this.candidates
        .filter((card) => {
          return !selected.includes(card) &&
            card.dataset.recommendationKind === kind &&
            card.dataset.productId !== this.currentProductId &&
            !cartProductIds.has(card.dataset.productId);
        })
        .sort((first, second) => {
          const difference = this.scoreCandidate(second, context, selected) - this.scoreCandidate(first, context, selected);
          if (difference !== 0) return difference;
          return Number(first.dataset.bestsellerRank || 99) - Number(second.dataset.bestsellerRank || 99);
        })[0];

      plan.forEach((kind) => {
        const card = pickCandidate(kind);
        if (card) selected.push(card);
      });

      while (selected.length < 2) {
        const fallback = pickCandidate('scent');
        if (!fallback) break;
        selected.push(fallback);
      }

      selected.forEach((card, index) => {
        card.style.order = String(index + 1);
        card.hidden = false;
      });
      this.section.hidden = selected.length === 0;
    }

    async initialize() {
      this.selectRecommendations(await this.fetchCart());
    }

    announce(message) {
      if (this.status) this.status.textContent = message;
    }

    async addProduct(button) {
      const variantId = Number(button.dataset.variantId || 0);
      if (!variantId || button.disabled) return;

      const label = button.querySelector('span');
      const originalLabel = label ? label.textContent : 'Hinzufügen';
      button.disabled = true;
      if (label) label.textContent = 'Wird hinzugefügt …';

      try {
        const response = await fetch(this.cartAddUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });
        if (!response.ok) throw new Error('Product could not be added');

        if (label) label.textContent = 'Hinzugefügt!';
        this.announce('Das Produkt wurde zum Warenkorb hinzugefügt.');

        const cartDrawer = document.querySelector('cart-notification');
        let cart = null;
        if (cartDrawer && typeof cartDrawer.refresh === 'function') {
          cart = await cartDrawer.refresh({ silent: true });
          if (typeof cartDrawer.setActiveElement === 'function') cartDrawer.setActiveElement(button);
          if (typeof cartDrawer.open === 'function') cartDrawer.open();
        }
        if (!cart) cart = await this.fetchCart();
        this.selectRecommendations(cart);
      } catch (error) {
        if (label) label.textContent = 'Bitte erneut versuchen';
        this.announce('Dieses Produkt konnte gerade nicht hinzugefügt werden.');
      } finally {
        window.setTimeout(() => {
          button.disabled = false;
          if (label && document.body.contains(button)) label.textContent = originalLabel;
        }, 1200);
      }
    }
  }

  const initializeSections = (scope = document) => {
    scope.querySelectorAll('[data-md-product-cross-sell]').forEach((section) => {
      if (section.dataset.mdInitialized === 'true') return;
      section.dataset.mdInitialized = 'true';
      new MeltProductCrossSell(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeSections(), { once: true });
  } else {
    initializeSections();
  }

  document.addEventListener('shopify:section:load', (event) => initializeSections(event.target));
})();
